import React, { useState } from "react";
import { Check, Star, ShieldCheck, AlertCircle } from "lucide-react";

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState("annual"); // annual / monthly

  const tiers = [
    {
      name: "Starter",
      desc: "For fast-growing mid-market supply networks needing foundational risk reviews.",
      priceAnnual: 799,
      priceMonthly: 949,
      features: [
        "Up to 50 active vendor scorecards",
        "Standard financial stability tracking",
        "Automated PDF document reminders",
        "Slack & Email real-time triggers",
        "Standard email support (24hr response)"
      ]
    },
    {
      name: "Growth",
      desc: "Comprehensive automated auditing, contract terms extraction, and live compliance monitoring.",
      priceAnnual: 1890,
      priceMonthly: 2190,
      features: [
        "Up to 250 active vendor scorecards",
        "Advanced Risk Intelligence & Alerts",
        "AI Contract Lifecycle Terms extraction",
        "SaaS Spend overlap identification",
        "Dedicated Integration Support webhook set"
      ],
      featured: true
    },
    {
      name: "Enterprise",
      desc: "Unrestricted custom platform intelligence layer built for complex multi-national operations.",
      priceAnnual: "Custom",
      priceMonthly: "Custom",
      features: [
        "Unrestricted active vendor scorecards",
        "Custom 3rd-party accounting triggers (SAP, Oracle)",
        "Automated custom KYC/AML verification",
        "Immutable Audit-ready compliance exports",
        "24/7 Priority SLA response + account manager"
      ]
    }
  ];

  const faqs = [
    { q: "How does the real-time scoring data synchronize?", a: "Aroveniq establishes event listeners via secure webhooks directly matching ERP systems like Coupa, SAP Ariba, or Zoho Books. Updates process instantly when files are uploaded." },
    { q: "Are we constrained by a minimum multi-year commitment?", a: "No. While our annual tier carries a premium 15% discount, you can select month-to-month contracts and terminate with standard 30-day notice safely." },
    { q: "How long does automated onboarding take to configure?", a: "Standard Starter and Growth setups launch in under 24 hours. Custom Enterprise database integrations generally validate completely within 14 business days." }
  ];

  return (
    <div className="bg-[#05050A] text-white min-h-screen py-24 relative overflow-hidden" data-testid="pricing-page">
      
      {/* Background glow overlay */}
      <div className="absolute top-20 right-1/4 w-[450px] h-[450px] bg-[#7C3AED]/5 rounded-full blur-[130px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Block */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-[#EA7C3D] tracking-wide">
            <ShieldCheck className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>SaaS Pricing and Frameworks</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter">Predictable Enterprise Sourcing Plans</h1>
          <p className="text-[#ABA3C2] text-base sm:text-lg max-w-2xl mx-auto">
            Zero hidden charges. Choose standard plans tailored for specialized compliance reviews, spend audits, and automated workflows.
          </p>

          {/* Toggle billing period */}
          <div className="pt-4 flex items-center justify-center space-x-4">
            <span className={`text-sm ${billingPeriod === "monthly" ? "text-white font-semibold" : "text-zinc-500"}`}>Monthly</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === "annual" ? "monthly" : "annual")}
              data-testid="billing-period-toggle"
              className="w-12 h-6 rounded-full bg-white/10 p-0.5 transition-colors relative flex items-center"
            >
              <div className={`w-5 h-5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] transform transition-transform ${billingPeriod === "annual" ? "translate-x-6" : "translate-x-0"}`} />
            </button>
            <span className={`text-sm ${billingPeriod === "annual" ? "text-white font-semibold" : "text-zinc-500"} flex items-center space-x-2`}>
              <span>Annual</span>
              <span className="text-[10px] bg-[#EA7C3D]/20 text-[#EA7C3D] px-2 py-0.5 rounded-full font-bold">Save 15%</span>
            </span>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {tiers.map((tier, i) => (
            <div
              key={i}
              className={`p-8 rounded-3xl bg-[#14101F]/80 border backdrop-blur-xl relative overflow-hidden flex flex-col justify-between ${
                tier.featured ? "border-[#7C3AED] shadow-[0_0_30px_rgba(124,58,237,0.2)]" : "border-white/10"
              }`}
              data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
            >
              {tier.featured && (
                <div className="absolute top-0 right-0 bg-[#EA7C3D] text-white text-[10px] font-bold px-4 py-1 rounded-bl-xl font-mono uppercase tracking-widest">
                  Popular
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold font-display text-white">{tier.name}</h3>
                  <p className="text-xs text-[#ABA3C2] mt-2 min-h-[40px] leading-relaxed font-body">{tier.desc}</p>
                </div>

                <div className="border-y border-white/5 py-4">
                  <span className="text-4xl font-black font-display text-white">
                    {typeof tier.priceAnnual === "string" ? (
                      tier.priceAnnual
                    ) : (
                      billingPeriod === "annual" ? `$${tier.priceAnnual}` : `$${tier.priceMonthly}`
                    )}
                  </span>
                  {typeof tier.priceAnnual === "number" && (
                    <span className="text-xs text-zinc-500 font-medium ml-1">/ month billed {billingPeriod}</span>
                  )}
                </div>

                {/* Features list */}
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-xs text-zinc-300">
                      <Check className="w-4 h-4 text-[#EA7C3D] shrink-0 mr-2 mt-0.5" />
                      <span className="leading-normal">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-8">
                <a
                  href="/contact"
                  className={`block w-full py-3 rounded-full text-center text-xs font-bold transition-all ${
                    tier.featured
                      ? "bg-gradient-to-r from-[#7C3AED] to-[#EA7C3D] text-white hover:shadow-lg"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                >
                  {tier.name === "Enterprise" ? "Contact Sourcing Experts" : "Get Started Now"}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* FAQs */}
        <div className="max-w-4xl mx-auto space-y-8" data-testid="pricing-faq-section">
          <h2 className="text-2xl font-bold font-display tracking-tight text-center">Frequently Answered Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {faqs.map((faq, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                <h4 className="text-sm font-bold text-white font-display">{faq.q}</h4>
                <p className="text-xs text-[#ABA3C2] leading-relaxed font-body">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}